import { useQuery } from "@tanstack/react-query";
import { OfferRemoveResponse, StoreOffersRequest, StoreOffersResponse, StoreOffersView, type StoreOffersRequestInput, type StoreOffersState } from "@townsy/contracts";
import { API_BASE, apiFetch, isApiError } from "./client";
import { isRewardsUnavailable } from "./rewards";

/**
 * "Add an offer" on one screen (ADR-0027): read the owner's offers with their one plain status, and save them with one
 * command. The server re-checks ownership, the store's live design and every rule, sends the offers to a Townsy reviewer
 * and starts them by itself once they passed review and are paid for. The browser never decides what goes live, never
 * sends a price for the payment and never marks anything paid: payment is the existing card-only checkout.
 */
const enc = encodeURIComponent;

export const storeOffersKeys = {
  all: ["store-offers"] as const,
  one: (propertyId: string) => ["store-offers", propertyId] as const,
};

export const fetchStoreOffers = (propertyId: string) => apiFetch(`${API_BASE}/stores/${enc(propertyId)}/offers`, { schema: StoreOffersView, cache: "no-cache" });

/** One press of the screen's button. `idempotencyKey` is made once per press (a retry of the same press reuses it). */
export const putStoreOffers = (propertyId: string, body: StoreOffersRequestInput, idempotencyKey: string) =>
  apiFetch(`${API_BASE}/stores/${enc(propertyId)}/offers`, {
    method: "PUT",
    body: StoreOffersRequest.parse(body),
    headers: { "idempotency-key": idempotencyKey },
    schema: StoreOffersResponse,
  });

/**
 * "Remove this offer" (owner; the store screen and the advertiser page): the offer is retired for good; the budget stays.
 * Removing an offer that is already removed changes nothing, so a retried press is harmless.
 */
export const postRemoveOffer = (campaignId: string, offerId: string) =>
  apiFetch(`${API_BASE}/campaigns/${enc(campaignId)}/offers/${enc(offerId)}/remove`, { method: "POST", body: {}, schema: OfferRemoveResponse });

export function newStoreOffersKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `offers-${crypto.randomUUID()}`;
  return `offers-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Re-read while something happens elsewhere (a reviewer decides, visitors earn coins); quiet otherwise. */
export function storeOffersPollMs(state: StoreOffersState | undefined): number | false {
  if (state === "checking") return 30_000;
  if (state === "live") return 60_000;
  if (state === "none" || state === "no_offers") return false;
  return false;
}

const noAuthRetry = (count: number, err: unknown) => !(isApiError(err) && [401, 403, 404, 422].includes(err.status)) && !isRewardsUnavailable(err) && count < 2;

export function useStoreOffersQuery(propertyId: string | null) {
  return useQuery({
    queryKey: storeOffersKeys.one(propertyId ?? "none"),
    queryFn: () => fetchStoreOffers(propertyId as string),
    enabled: Boolean(propertyId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: (q) => (q.state.error ? false : storeOffersPollMs(q.state.data?.status.state)),
    retry: noAuthRetry,
  });
}
